import type { Observation } from '@medplum/fhirtypes'
import type { ExtendedObservation } from '@seen/core/models/observation'
import { IconChartHistogram } from '@tabler/icons-react'
import type { JSX } from 'react'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { ObservationHistoryChart } from '#src/components/observation/ObservationHistoryChart.tsx'
import { actionButtonClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { ObservationHistory } from '#src/components/rich-text-editor/ObservationNode/ObservationHistory.tsx'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'

export interface ObservationHistoryPopoverProps {
  attributes: ObservationNodeAttributes
  subject: Observation['subject']
  // Earlier observations of the node's concept, newest first.
  history: ExtendedObservation[]
  // Held by the node's frame, which can open it from the node itself too.
  open: boolean
  onOpenChange: (open: boolean) => void
  // Opens the node's details; absent where there are none yet.
  onOpenDetails?: () => void
}

// The "History" action on a node: its earlier values at a glance, a trend for
// numbers and the recent answers otherwise. Not modal, like the node's
// pickers, so the note stays live behind it.
export function ObservationHistoryPopover({
  attributes,
  subject,
  history,
  onOpenChange,
  onOpenDetails,
  open,
}: ObservationHistoryPopoverProps): JSX.Element {
  const changeOpen = onOpenChange
  const coding = { system: attributes.system, code: attributes.code }

  return (
    <Popover open={open} onOpenChange={changeOpen} modal={false}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="History"
              className={actionButtonClass()}
            >
              <IconChartHistogram className="size-3.5" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>History</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="end"
        className="flex w-80 flex-col gap-3 p-3 text-sm"
        aria-label={`${attributes.codingDisplay} history`}
      >
        {attributes.scaleType === 'Qn' && subject ? (
          <ObservationHistoryChart
            subject={subject}
            coding={coding}
            variant="compact"
          />
        ) : (
          <ObservationHistory observations={history} coding={coding} />
        )}
        {onOpenDetails && (
          <button
            type="button"
            onClick={() => {
              changeOpen(false)
              onOpenDetails()
            }}
            className="self-start text-xs text-blue-600 hover:underline focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-hidden"
          >
            Open details
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
